<template>
	<div class="ao-button-toggle-wrapper">
		<label
			v-if="label"
			class="ao-button-toggle__label"
		>
			{{ label }}
		</label>

		<v-btn-toggle
			:modelValue="modelValue"
			:mandatory="mandatory"
			class="ao-button-toggle"
			:class="[`ao-button-toggle_${size}-size`]"
			@update:modelValue="onUpdateModelValue"
		>
			<AoBtn
				v-for="item in renderItems"
				v-bind="item.props"
				:key="item.key"
			/>
		</v-btn-toggle>
	</div>
</template>

<script setup>
import { computed } from 'vue'

import AoBtn from './AoBtn.vue'

const props = defineProps({
	modelValue: {
		type: [String, Number, Boolean],
		default: null,
	},
	/**
	 * @param {Object} item
	 * @param {string} item[itemTitle]
	 * @param {string} item[itemValue]
	 * @param {string} [item.color]
	 * @param {string} [item.icon]
	 */
	items: {
		type: Array,
		default: () => [],
		validator: (value, { itemTitle, itemValue }) => {
			return value.every((item) => !!item[itemValue] && !!item[itemTitle])
		},
	},
	itemTitle: {
		type: String,
		default: 'title',
	},
	itemValue: {
		type: String,
		default: 'value',
	},
	size: {
		type: String,
		default: 'default',
		validator: (v) => ['x-small', 'small', 'default', 'large', 'x-large'].includes(v),
	},
	activeColor: {
		type: String,
		default: 'default',
		validator: (v) => ['default', 'primary', 'accent', 'success', 'warning', 'error'].includes(v),
	},
	showIconPolicy: {
		type: String,
		default: 'auto',
		validator: (v) => ['auto', 'onlyActive', 'never'].includes(v),
	},
	label: {
		type: String,
		default: '',
	},

	activeIconName: String,

	mandatory: {
		type: Boolean,
		default: false,
	},

	devMode: {
		type: Boolean,
		default: false,
	},
})

const emit = defineEmits({
	'update:modelValue': null,
})

const renderItems = computed(() => {
	return props.items.map((item) => {
		const value = item[props.itemValue]
		const isActive = props.modelValue === value
		const text = item[props.itemTitle]

		const color = item.color
		const textColorClass = (!isActive && !!color) ? `text-${color}` : ''

		const activeColor = color ? color : props.activeColor

		const iconName = isActive && props.activeIconName ? props.activeIconName : item.icon

		const showIcon =
			(props.showIconPolicy === 'auto' && !!item.icon) ||
			(props.showIconPolicy === 'onlyActive' && isActive && !!(props.activeIconName || item.icon))

		return {
			key: value,
			props: {
				value,
				prependIconName: showIcon ? iconName : null,
				text,
				color,
				active: isActive,
				activeColor,
				class: [textColorClass],
			},
		}
	})
})

const onUpdateModelValue = (value) => {
	emit('update:modelValue', value)
}
</script>

<style lang="scss">

@mixin button-toggle-size($name, $height, $button-height, $font-size, $padding, $radius) {
	&_#{$name}-size {
		height: $height !important;
		padding: calc($padding/2);
		border-radius: $radius;

		.ao-btn {
			height: $button-height !important;
			border-radius: $radius;
			font-size: $font-size;
		}
	}
}

.ao-button-toggle-wrapper {
	display: inline-flex;
	flex-direction: column;
	gap: 6px;
	align-items: flex-start;
}

.ao-button-toggle__label {
	font-size: 12px;
	color: rgb(var(--v-theme-secondary));
}

.ao-button-toggle {
	align-items: center;
	background-color: rgb(var(--v-theme-background));

	.ao-btn {
		background-color: transparent;
		line-height: 1;
	}

	@include button-toggle-size('x-small', 26px, 22px, 10px, 6px, 3px);
	@include button-toggle-size('small', 36px, 28px, 11px, 8px, 4px);
	@include button-toggle-size('default', 44px, 36px, 12px, 8px, 6px);
	@include button-toggle-size('large', 48px, 40px, 14px, 8px, 8px);
	@include button-toggle-size('x-large', 52px, 44px, 16px, 8px, 10px);
}
</style>
